/**
 * Org admin TanStack Query hooks (issue #275 — multi-tenancy Phase 4).
 *
 * Wraps the `/api/admin/me/orgs` (Phase 3) and `/api/admin/organizations/*`
 * (Phase 4) endpoints so the SPA's OrgSwitcher + OrganizationDetailPage
 * consume one consistent surface.
 *
 * Cache strategy:
 *   - `useMyOrgs`         — keyed on user; invalidated when the user
 *                            switches current org so the badge in the
 *                            top bar re-renders with the new pointer.
 *   - `useOrgMembers(id)` — keyed on org id; invalidated by every
 *                            mutation in this file plus by the
 *                            `useSetCurrentOrg` switch so a page-refresh
 *                            after a switch loads the right org's
 *                            members.
 *
 * Mutations invalidate the BROAD `me` cache too, because changing the
 * current org cascades into org-scoped lists everywhere — `useSites`
 * for example reads sites in the actor's orgs. We could narrow this in
 * a follow-up but the current shape favours correctness over chatter.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreateInviteInput,
  CreateInviteResponse,
  ListInvitationsResponse,
  MeOrgsResponse,
  OrganizationMembersResponse,
  OrgMemberResponse,
  OrgMemberRoleLiteral,
  PublicInvitePreviewResponse,
  SetCurrentOrgResponse,
  UpdateOrganizationInput,
  UpdateOrganizationResponse,
  UpdateOrgMemberRoleInput,
} from "@ottopilot/shared";
import { apiDelete, apiGet, apiPatch, apiPost } from "../lib/api";
import type { ApiClientError } from "../lib/api";
import { queryKeys } from "./queryKeys";

/**
 * Query keys for the org admin surface. Co-located with the hooks rather
 * than threaded through `queryKeys.ts` because no other consumer
 * references them directly — every invalidate happens inside this file.
 */
export const orgsQueryKeys = {
  myOrgs: ["orgs", "me"] as const,
  members: (orgId: string) => ["orgs", "members", orgId] as const,
  invitations: (orgId: string) => ["orgs", "invitations", orgId] as const,
  /**
   * Public invite preview (issue #276 — multi-tenancy Phase 5). Keyed
   * on the token so an invite link refresh refetches; the SignupPage
   * is the only consumer.
   */
  invitePreview: (token: string) => ["orgs", "invite-preview", token] as const,
} as const;

/**
 * Fetch the user's org memberships (mirrors Phase 3's
 * `GET /api/admin/me/orgs`). Powers the OrgSwitcher dropdown and the
 * "Currently acting as" chip.
 */
export function useMyOrgs() {
  return useQuery<MeOrgsResponse, ApiClientError>({
    queryKey: orgsQueryKeys.myOrgs,
    queryFn: () => apiGet<MeOrgsResponse>("/api/admin/me/orgs"),
  });
}

/**
 * Switch the user's current org. The mutation invalidates BOTH the
 * `me/orgs` cache (so the OrgSwitcher highlight + chip re-render) AND
 * the broader `auth.me` cache (so any cascade into org-scoped lists
 * picks up the new pointer on next read).
 *
 * Sites + experiments + content lists are scoped by org server-side via
 * the Phase 2 chokepoint; invalidating their query roots forces a
 * refetch after the switch.
 */
export function useSetCurrentOrg() {
  const qc = useQueryClient();
  return useMutation<SetCurrentOrgResponse, ApiClientError, { organizationId: string }>({
    mutationFn: (input) => apiPost<SetCurrentOrgResponse>("/api/admin/me/current-org", input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: orgsQueryKeys.myOrgs });
      void qc.invalidateQueries({ queryKey: queryKeys.auth.me });
      void qc.invalidateQueries({ queryKey: queryKeys.sites.all });
      void qc.invalidateQueries({ queryKey: queryKeys.experiments.all });
      void qc.invalidateQueries({ queryKey: queryKeys.contentItems.all });
      void qc.invalidateQueries({ queryKey: queryKeys.pages.all });
      void qc.invalidateQueries({ queryKey: queryKeys.domains.all });
    },
  });
}

/**
 * Fetch the org header + members list. Returns the full envelope so the
 * detail page can render heading + slug + table from one round-trip.
 */
export function useOrgMembers(orgId: string) {
  return useQuery<OrganizationMembersResponse, ApiClientError>({
    queryKey: orgsQueryKeys.members(orgId),
    queryFn: () => apiGet<OrganizationMembersResponse>(`/api/admin/organizations/${orgId}/members`),
    enabled: orgId.length > 0,
  });
}

/**
 * Rename the org. Invalidates the org members cache (so the heading +
 * slug re-render) and the my-orgs cache (so the OrgSwitcher entry shows
 * the new name without a page reload).
 */
export function useUpdateOrganization(orgId: string) {
  const qc = useQueryClient();
  return useMutation<UpdateOrganizationResponse, ApiClientError, UpdateOrganizationInput>({
    mutationFn: (input) =>
      apiPatch<UpdateOrganizationResponse>(`/api/admin/organizations/${orgId}`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: orgsQueryKeys.members(orgId) });
      void qc.invalidateQueries({ queryKey: orgsQueryKeys.myOrgs });
    },
  });
}

/**
 * Promote / demote a member. Invalidates the org members cache only —
 * role changes don't affect the OrgSwitcher.
 *
 * The mutation accepts both the userId AND the new role so the SPA can
 * render two distinct buttons (Promote / Demote) without re-deriving
 * the inverse role at the call site.
 */
export function useUpdateOrgMemberRole(orgId: string) {
  const qc = useQueryClient();
  return useMutation<
    OrgMemberResponse,
    ApiClientError,
    { userId: string; role: OrgMemberRoleLiteral }
  >({
    mutationFn: ({ userId, role }) => {
      const body: UpdateOrgMemberRoleInput = { role };
      return apiPatch<OrgMemberResponse>(
        `/api/admin/organizations/${orgId}/members/${userId}`,
        body,
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: orgsQueryKeys.members(orgId) });
    },
  });
}

/**
 * Remove a member. Invalidates the org members cache. Additionally
 * invalidates the my-orgs cache because removing the CURRENT actor from
 * the org would drop the org from their switcher dropdown — the
 * detail-page guard (membership chokepoint) will then 404 the next read,
 * and `RequireAuth` / the page's own error branch can redirect.
 */
export function useRemoveOrgMember(orgId: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, { userId: string }>({
    mutationFn: ({ userId }) =>
      apiDelete<void>(`/api/admin/organizations/${orgId}/members/${userId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: orgsQueryKeys.members(orgId) });
      void qc.invalidateQueries({ queryKey: orgsQueryKeys.myOrgs });
    },
  });
}

/**
 * List pending invitations for an org (issue #276 — Phase 5). Powers
 * the "Pending invitations" table on the OrganizationDetailPage.
 */
export function useOrgInvitations(orgId: string) {
  return useQuery<ListInvitationsResponse, ApiClientError>({
    queryKey: orgsQueryKeys.invitations(orgId),
    queryFn: () => apiGet<ListInvitationsResponse>(`/api/admin/organizations/${orgId}/invites`),
    enabled: orgId.length > 0,
  });
}

/**
 * Create an invite for an org (issue #276 — Phase 5). The response is
 * a discriminated union:
 *
 *   - `kind: "existing-user"` — user was already in the system; a
 *     membership was inserted directly. SPA refreshes the members
 *     table.
 *   - `kind: "invitation"`    — a token-bearing invitation was created.
 *     SPA shows the inviteUrl in a copy-to-clipboard dialog.
 *
 * Both branches invalidate the members AND invitations caches so the
 * UI re-renders against the canonical post-write state.
 */
export function useCreateOrgInvite(orgId: string) {
  const qc = useQueryClient();
  return useMutation<CreateInviteResponse, ApiClientError, CreateInviteInput>({
    mutationFn: (input) =>
      apiPost<CreateInviteResponse>(`/api/admin/organizations/${orgId}/invites`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: orgsQueryKeys.members(orgId) });
      void qc.invalidateQueries({ queryKey: orgsQueryKeys.invitations(orgId) });
    },
  });
}

/**
 * Revoke a pending invitation (issue #276 — Phase 5). Hard-deletes
 * the row server-side; the audit log entry survives. Invalidates the
 * invitations cache only — members aren't affected.
 */
export function useRevokeOrgInvitation(orgId: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, { invitationId: string }>({
    mutationFn: ({ invitationId }) =>
      apiDelete<void>(`/api/admin/organizations/${orgId}/invites/${invitationId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: orgsQueryKeys.invitations(orgId) });
    },
  });
}

/**
 * Public invite preview (issue #276 — Phase 5). Used by the SignupPage
 * when the URL carries `?invite=<token>`. No auth required — the
 * endpoint returns 404 for missing / expired / accepted tokens.
 *
 * `retry: false` so a 404 doesn't burn three retries before the page
 * surfaces the error.
 */
export function useInvitePreview(token: string | null) {
  return useQuery<PublicInvitePreviewResponse, ApiClientError>({
    queryKey: orgsQueryKeys.invitePreview(token ?? ""),
    queryFn: () =>
      apiGet<PublicInvitePreviewResponse>(`/api/public/invites/${encodeURIComponent(token ?? "")}`),
    enabled: token !== null && token.length > 0,
    retry: false,
  });
}
